import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Eye, EyeOff, X, Save, Loader2, FileText, Image, Video } from 'lucide-react';
import './AdminBlogs.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    cover_image: '',
    content: '',
    excerpt: '',
    video_url: '',
    published: false
  });

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/blogs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBlogs(data);
      }
    } catch (error) {
      console.error('Failed to fetch blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingBlog(null);
    setFormData({
      title: '',
      slug: '',
      cover_image: '',
      content: '',
      excerpt: '',
      video_url: '',
      published: false
    });
    setShowModal(true);
  };

  const openEditModal = (blog) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title,
      slug: blog.slug,
      cover_image: blog.cover_image,
      content: blog.content,
      excerpt: blog.excerpt || '',
      video_url: blog.video_url || '',
      published: blog.published
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('adminToken');
      const url = editingBlog 
        ? `${API_URL}/api/admin/blogs/${editingBlog.id}`
        : `${API_URL}/api/admin/blogs`;
      
      const response = await fetch(url, {
        method: editingBlog ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        fetchBlogs();
        setShowModal(false);
      }
    } catch (error) {
      console.error('Failed to save blog:', error);
    } finally {
      setSaving(false);
    }
  };

  const togglePublished = async (blog) => {
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${API_URL}/api/admin/blogs/${blog.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ published: !blog.published })
      });
      fetchBlogs();
    } catch (error) {
      console.error('Failed to toggle publish status:', error);
    }
  };

  const deleteBlog = async (blogId) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/blogs/${blogId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchBlogs();
      }
    } catch (error) {
      console.error('Failed to delete blog:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Draft';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="admin-blogs" data-testid="admin-blogs">
      <div className="page-header">
        <div>
          <h1>Blog Posts</h1>
          <p>Manage your blog articles</p>
        </div>
        <button className="add-btn" onClick={openCreateModal} data-testid="add-blog-btn">
          <Plus size={18} />
          <span>New Post</span>
        </button>
      </div>

      {loading ? (
        <div className="loading-container">
          <Loader2 className="spin" size={32} />
          <span>Loading blogs...</span>
        </div>
      ) : blogs.length === 0 ? (
        <div className="empty-container">
          <FileText size={48} />
          <h3>No blog posts yet</h3>
          <p>Create your first blog post to share with your audience.</p>
          <button className="add-btn" onClick={openCreateModal}>
            <Plus size={18} />
            <span>Create Post</span>
          </button>
        </div>
      ) : (
        <div className="blogs-list">
          {blogs.map((blog) => (
            <div key={blog.id} className="blog-item" data-testid={`blog-item-${blog.id}`}>
              <div className="blog-thumb">
                {blog.cover_image ? (
                  <img src={blog.cover_image} alt={blog.title} />
                ) : (
                  <div className="no-thumb"><Image size={24} /></div>
                )}
              </div>
              <div className="blog-info">
                <h3>{blog.title}</h3>
                <div className="blog-meta">
                  <span className={`status-badge ${blog.published ? 'published' : 'draft'}`}>
                    {blog.published ? 'Published' : 'Draft'}
                  </span>
                  <span className="date">{formatDate(blog.publish_date)}</span>
                </div>
              </div>
              <div className="blog-actions">
                <button className="action-btn" onClick={() => togglePublished(blog)} title={blog.published ? 'Unpublish' : 'Publish'}>
                  {blog.published ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
                <button className="action-btn" onClick={() => openEditModal(blog)}>
                  <Edit2 size={16} />
                </button>
                <button className="action-btn delete" onClick={() => deleteBlog(blog.id)}>
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Blog Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingBlog ? 'Edit Blog Post' : 'New Blog Post'}</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="blog-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Enter blog title"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Slug</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="auto-generated-from-title"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Cover Image URL *</label>
                  <input
                    type="url"
                    value={formData.cover_image}
                    onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Video URL (Optional)</label>
                  <input
                    type="url"
                    value={formData.video_url}
                    onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                    placeholder="https://youtube.com/watch?v=..."
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Excerpt</label>
                <input
                  type="text"
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  placeholder="Short description for listing page"
                />
              </div>

              <div className="form-group">
                <label>Content * (HTML supported)</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Write your blog content here..."
                  rows={12}
                  required
                />
              </div>

              <div className="form-group checkbox-container">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.published}
                    onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                  />
                  <span>Publish immediately</span>
                </label>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={saving}>
                  {saving ? (
                    <><Loader2 size={16} className="spin" /><span>Saving...</span></>
                  ) : (
                    <><Save size={16} /><span>{editingBlog ? 'Update' : 'Create'} Post</span></>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlogs;
